import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Seo } from "@/components/seo/Seo";
import { Header } from "@/components/Header";
import { ArrowUpRight, Lightbulb, Target, Brain, Globe, CheckCircle2 } from "lucide-react";

const AuthorsLanding1 = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [bookTitle, setBookTitle] = useState("");
  const [methodType, setMethodType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const scrollToWaitlist = () => {
    const el = document.getElementById("waitlist");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAlreadyRegistered(false);

    try {
      const { data, error } = await supabase.functions.invoke('submit-waitlist', {
        body: { 
          name, 
          email, 
          userType: 'expert',
          website,
          linkedin,
          bookTitle,
          methodType
        }
      });

      if (error) throw error;

      setIsSubmitted(true);
      setAlreadyRegistered(Boolean(data?.alreadyRegistered));

      if (!data?.alreadyRegistered) {
        toast({
          title: "Success!",
          description: "Thank you for your application. We'll review it and reach out personally.",
        });
      }
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: (error instanceof Error ? error.message : "Request failed") || "Failed to submit your application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo 
        title="For Authors - Turn Your Book into an AI Method | Applyt"
        description="Applyt turns your book or framework into an AI method lens that helps readers produce finished work, not just highlights. Apply for the author beta."
      />
      <Header showNavigation={false} hideUnauthenticatedActions hideAuthenticatedActions />
      
      {/* Tagline & Navigation */}
      <div className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground font-medium">Method-to-Work Engine</p>
          <nav className="flex items-center gap-6">
            <a href="#gap" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Why this</a>
            <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How it works</a>
            <a href="#waitlist" className="text-sm text-muted-foreground hover:text-foreground transition-colors">For authors</a>
          </nav>
        </div>
      </div>
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="py-12 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-4">
              <span className="px-2 py-1 rounded-full border border-border text-[10px]">Authors & Method Owners</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Turn your book into an{" "}
              <span className="text-primary">
                AI method
              </span>{" "}
              that does the work with your reader
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mb-6 max-w-xl">
              Applyt turns your frameworks into expert <strong className="text-foreground">lenses</strong>: AI-powered versions of your method
              that apply your book directly to a reader's situation and output finished work:
              IC briefs, KPI trees, launch plans, hiring scorecards, risk matrices, and more.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <Button 
                onClick={scrollToWaitlist}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                  Become an Applyt Expert
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">First cohort: 30 authors</strong> across leadership, business development & GTM.
              </p>
            </div>
          </div>

          {/* Hero Card */}
          <Card className="bg-gradient-to-br from-primary/10 via-background to-primary/5 border-border/50 shadow-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 rounded-full bg-background/70 border border-border text-xs uppercase tracking-wider text-muted-foreground">
                  Example lens
                </span>
                <span className="px-3 py-1 rounded-full bg-background/70 border border-border/50 text-xs text-muted-foreground flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  Method-to-Work
                </span>
              </div>
              
              <div className="bg-background/80 border border-border/80 rounded-xl p-4 mb-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Your book</div>
                <div className="font-semibold mb-2">"Strategic Sprints for GTM Teams"</div>
                <p className="text-xs text-muted-foreground mb-3">
                  Reader describes their situation once: Applyt outputs:
                </p>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 mt-1.5 rounded-full bg-gradient-to-r from-orange-500 to-primary flex-shrink-0" />
                    <span>IC brief aligned to your doctrine</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 mt-1.5 rounded-full bg-gradient-to-r from-orange-500 to-primary flex-shrink-0" />
                    <span>Prioritized sprint backlog based on your rules</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 mt-1.5 rounded-full bg-gradient-to-r from-orange-500 to-primary flex-shrink-0" />
                    <span>Risk matrix using your scoring model</span>
                  </li>
                </ul>
                
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className="bg-background border border-border/80 rounded-lg p-2">
                    <div className="text-[10px] text-muted-foreground">Sessions/mo</div>
                    <div className="font-semibold text-sm">420</div>
                  </div>
                  <div className="bg-background border border-border/80 rounded-lg p-2">
                    <div className="text-[10px] text-muted-foreground">Revenue</div>
                    <div className="font-semibold text-sm">$3.8k</div>
                  </div>
                  <div className="bg-background border border-border/80 rounded-lg p-2">
                    <div className="text-[10px] text-muted-foreground">Premium</div>
                    <div className="font-semibold text-sm">24%</div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span><strong className="text-foreground">Your method</strong> becomes an engine for decisions</span>
                <span className="px-2 py-1 rounded-full border border-border">No extra calls</span>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Problem Section */}
        <section id="gap" className="py-12 border-t border-border">
          <div className="max-w-2xl mb-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">The gap</p>
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">
              Your book changes minds. But does it change calendars?
            </h2>
            <p className="text-muted-foreground">
              Readers love your ideas: then stall when it's time to apply them. Old markets treated{" "}
              <strong className="text-foreground">content</strong> (books, courses) and{" "}
              <strong className="text-foreground">application</strong> (workshops, consulting)
              as separate worlds. Applyt closes that gap.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-secondary/30 border-border">
              <CardContent className="p-5">
                <ul className="space-y-3">
                  {[
                    "Readers bookmark, highlight, and promise to 'come back later': but don't implement.",
                    "Teams re-read chapters instead of making decisions on live projects.",
                    "You end up re-teaching the same process to every client, live, 1:1.",
                    "Generic AI chats can talk about your topic, but not with your doctrine, citations, or guardrails."
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <span className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            
            <Card className="bg-secondary/30 border-border">
              <CardContent className="p-5 text-sm text-muted-foreground">
                <p className="mb-3">
                  <strong className="text-foreground">Applyt closes the gap between consuming expert content and applying it.</strong>{" "}
                  Instead of static knowledge, it delivers real-time application – your method executed on demand.
                </p>
                <p>
                  This erases the line between <strong className="text-foreground">learning and doing</strong>. 
                  Your work stops living only in pages and slides, and starts living inside the decisions your readers make every day.
                </p>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8 text-center">
            <Button onClick={scrollToWaitlist} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-12 border-t border-border">
          <div className="max-w-2xl mb-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">From content to application</p>
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">
              From book to AI lens and then finished work
            </h2>
            <p className="text-muted-foreground">
              Applyt transforms your method into an expert lens that applies your rules, sources, and constraints –
              turning every reading session into a tiny workshop that outputs concrete deliverables.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                step: "Step 1",
                title: "You share your method",
                desc: "We work from your book, workbooks, templates, and real client examples to map your doctrine: the steps, constraints, and 'non-negotiables' that make your method uniquely yours."
              },
              {
                step: "Step 2",
                title: "Applyt builds your expert lens",
                desc: "Your method becomes a guided AI lens. Readers input their situation; the lens outputs artifacts that follow your structure: briefs, plans, matrices, scorecards, and more."
              },
              {
                step: "Step 3",
                title: "Readers apply & you scale",
                desc: "Readers run your method 24/7 without needing live calls. You earn usage revenue and route high-intent users into cohorts, workshops, or advisory."
              }
            ].map((item, i) => (
              <Card key={i} className="bg-secondary/30 border-border">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{item.step}</p>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button onClick={scrollToWaitlist} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Differentiators */}
        <section className="py-12 border-t border-border">
          <div className="max-w-2xl mb-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Why Applyt is different</p>
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">
              <span className="text-primary">
                New lines to cross
              </span>{" "}
              in the expert economy
            </h2>
            <p className="text-muted-foreground">
              Applyt isn't "ChatGPT with your face on it." It breaks four boundaries that held experts back from
              scaling impact without losing rigor.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                boundary: "Boundary 1",
                title: "Content vs. Application",
                desc: "Old markets separated content (books, courses) from application (workshops, consulting). Applyt closes the gap by delivering real-time application – your method executed on demand in each user's context.",
                highlight: "Every lens session becomes a working session, not just a reading session."
              },
              {
                boundary: "Boundary 2",
                title: "Generic AI vs. Expert guidance",
                desc: "Generic AI chats provide broad answers, but lack doctrine, citations, or governance. Applyt's experts bring specific methodology, guardrails, and trusted sources, combining AI scalability with the credibility of expert consulting.",
                highlight: "Users don't just hear about your topic: they work inside your way of thinking."
              },
              {
                boundary: "Boundary 3",
                title: "One-to-one vs. one-to-many",
                desc: "High-touch coaching and advisory don't naturally scale. Applyt turns your personalized processes into a digital product that can serve many users 24/7 without your direct involvement.",
                highlight: "Your role shifts from time-bound service provider to infrastructure for decisions."
              },
              {
                boundary: "Boundary 4",
                title: "AI agent build vs. buy",
                desc: "Agent builders either engineer domain logic in-house (costly, slow) or settle for generic models (risky). Applyt offers a third path: plug-and-play expert modules.",
                highlight: "Your method can power tools and platforms – opening new licensing opportunities."
              }
            ].map((item, i) => (
              <Card key={i} className="bg-secondary/30 border-border">
                <CardContent className="p-5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{item.boundary}</p>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{item.desc}</p>
                  <p className="text-xs text-foreground">{item.highlight}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button onClick={scrollToWaitlist} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Value for Authors */}
        <section className="py-12 border-t border-border">
          <div className="max-w-2xl mb-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">For authors & method owners</p>
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">What you gain</h2>
            <p className="text-muted-foreground">
              Applyt is built for non-fiction authors, coaches, and consultants who think like firms:
              IP + brand as the core asset.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                icon: Lightbulb,
                title: "New revenue stream",
                desc: "Readers and teams pay to run your lens when they need to produce work, not just read. You earn recurring usage revenue, not just launch spikes."
              },
              {
                icon: Target,
                title: "Higher conversion to premium",
                desc: "Your lens includes a built-in sales channel for cohorts, workshops, and advisory – triggered at moments of real progress and clarity."
              },
              {
                icon: Brain,
                title: "IP-aligned AI, not generic chat",
                desc: "Your method, your rules. The lens encodes your doctrine, sources, and boundaries instead of free-form prompts anyone can copy."
              },
              {
                icon: Globe,
                title: "Positioned as infrastructure",
                desc: "You're no longer just 'the author of X.' Your method becomes the operating layer teams and readers rely on for recurring decisions."
              }
            ].map((item, i) => (
              <Card key={i} className="bg-secondary/30 border-border">
                <CardContent className="p-4 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button onClick={scrollToWaitlist} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Fit Section */}
        <section className="py-12 border-t border-border">
          <div className="max-w-2xl mb-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Who it's for</p>
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">Is Applyt right for you?</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="bg-secondary/30 border-border">
              <CardContent className="p-5">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Perfect fit if you…
                </h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Have a non-fiction book, program, or framework that people already use in practice.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>See yourself as a brand + IP portfolio, not just someone who "wrote a book."</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Want leverage beyond 1:1 time without diluting your standards.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Are curious about AI but want your method encoded with rigor and governance.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card className="bg-secondary/30 border-border">
              <CardContent className="p-5">
                <h4 className="font-semibold mb-3">Probably not a fit if you…</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span>Don't yet have a defined method or repeatable process.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span>Are looking for a generic chatbot with your name on it.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span>Don't want your work to become part of teams' day-to-day decisions.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8 text-center">
            <Button onClick={scrollToWaitlist} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Application Form Section */}
        <section className="py-12 border-t border-border" id="waitlist">
          <div className="max-w-2xl mb-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Join the author cohort</p>
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">Apply for the Applyt author beta</h2>
            <p className="text-muted-foreground">
              We're onboarding an initial group of <strong className="text-foreground">30 authors</strong> across leadership, business development,
              GTM, and adjacent fields. Tell us about your method so we can assess fit.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Process Steps */}

            {/* Form */}
            <Card className="bg-secondary/30 border-border shadow-2xl">
              <CardContent className="p-5">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-sm">
                        Full name<span className="text-orange-500 ml-1">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-sm">
                        Email<span className="text-primary ml-1">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="website" className="text-sm">Website</Label>
                        <Input
                          id="website"
                          type="url"
                          placeholder="https://yoursite.com"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="linkedin" className="text-sm">LinkedIn</Label>
                        <Input
                          id="linkedin"
                          type="url"
                          placeholder="https://linkedin.com/in/..."
                          value={linkedin}
                          onChange={(e) => setLinkedin(e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="bookTitle" className="text-sm">
                        Book / method title<span className="text-primary ml-1">*</span>
                      </Label>
                      <Input
                        id="bookTitle"
                        type="text"
                        placeholder="Title of your book, flagship program, or framework"
                        value={bookTitle}
                        onChange={(e) => setBookTitle(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="methodType" className="text-sm">
                        Your method focuses on…<span className="text-primary ml-1">*</span>
                      </Label>
                      <Select value={methodType} onValueChange={setMethodType} disabled={isLoading} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Select the closest area" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="leadership">Leadership & culture</SelectItem>
                          <SelectItem value="business-dev">Business development & sales</SelectItem>
                          <SelectItem value="gtm">GTM & strategy</SelectItem>
                          <SelectItem value="coaching">Coaching & personal transformation</SelectItem>
                          <SelectItem value="ops">Operations & execution</SelectItem>
                          <SelectItem value="other">Something else</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>


                    <div className="space-y-3 pt-2">
                      <Button 
                        type="submit" 
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                        disabled={isLoading}
                      >
                        {isLoading ? "Submitting..." : "Become an Applyt Expert"}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        No obligation. We'll review your application and reach out personally if your method
                        is a strong fit for the initial author cohort.
                      </p>
                    </div>
                  </form>
                ) : (
                  <div className="py-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="space-y-2 text-left">
                        <h3 className="text-xl font-semibold">Thanks for your application!</h3>
                        <p className="text-muted-foreground">We'll be in touch soon.</p>
                      </div>
                      {alreadyRegistered && (
                        <div className="px-3 py-2 rounded-md border border-border bg-background text-sm text-muted-foreground">
                          Already registered
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
          <span>© {new Date().getFullYear()} Applyt. All rights reserved.</span>
          <span className="px-3 py-1 rounded-full border border-border">Authors cohort: applications open</span>
        </footer>
      </main>
    </div>
  );
};

export default AuthorsLanding1;
